import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import DefaultLayout from "../../layouts/DefaultLayout";
import { withRouter } from "react-router-dom";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Row, Table } from "antd";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { defaultCurrencyFormat, defaultNumberFormat } from "../../../utilities";

/**
 * Shows reports for quotes.
 */
@inject("context")
@withRouter
@observer
class Reports extends Component {
	componentDidMount() {
		this.props.context.reportsService.loadReportsList();
	}

	render() {
		const service = this.props.context.reportsService;

		const columns = [
			{
				title: "Date",
				dataIndex: "start_date",
				sorter: true,
				render: (date) => {
					return new Date(date).toLocaleDateString();
				},
				filterDropdown: ({
					setSelectedKeys,
					selectedKeys,
					confirm,
					clearFilters,
				}) => {
					return (
						<div style={{ padding: 8 }}>
							<DatePicker.RangePicker
								value={selectedKeys}
								onCalendarChange={(dates) => setSelectedKeys(dates)}
							/>
							<Button
								type="primary"
								onClick={confirm}
								icon={<SearchOutlined />}
								size="small"
							>
								Filter
							</Button>
							<Button size="small" onClick={clearFilters}>
								Reset filter
							</Button>
						</div>
					);
				},
				filterIcon: (filtered) => (
					<SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
				),
				// onFilter
			},
			{
				title: "Insurer",
				dataIndex: "insurer",
				sorter: true,
			},
			{
				title: "Product",
				dataIndex: "product_name",
				sorter: true,
			},
			{
				title: "Policy Number",
				dataIndex: "policy_number",
				sorter: true,
			},
			{
				title: "Premium Paid, excl. IPT",
				dataIndex: "gross_premium",
				sorter: true,
				render: (n) => {
					return defaultCurrencyFormat(n);
				},
			},
			{
				title: "Total Due/Refund",
				dataIndex: "totalDueRefund",
				sorter: true,
				render: (n, record) => {
					return record.mta || record.cancellation
						? defaultCurrencyFormat(record.totalDueRefund)
						: defaultCurrencyFormat(record.totalDueRefund);
				},
			},
			{
				title: "Firstname",
				dataIndex: "first_name",
				sorter: true,
			},
			{
				title: "Lastname",
				dataIndex: "last_name",
				sorter: true,
			},
			{
				title: "Address 1",
				dataIndex: "address1",
				sorter: true,
			},
			{
				title: "Address 2",
				dataIndex: "address2",
				sorter: true,
			},
			{
				title: "City or Town",
				dataIndex: "city",
				sorter: true,
			},
			{
				title: "Postcode",
				dataIndex: "postcode",
				sorter: true,
			},
			{
				title: "Status",
				dataIndex: "status",
				sorter: true,
				render: (v, record) => {
					return `${v}${record.mta ? " (MTA)" : ""}`;
				},
			},
		];

		return (
			<DefaultLayout
				layoutContentProps={{
					style: {
						maxWidth: "100%",
						paddingLeft: 40,
						paddingRight: 40,
					},
				}}
			>
				<Row>
					<Col xs={24}>
						<Card title={<h1>Reports</h1>}>
							<Row>
								<Col xs={24}>
									<Button
										className="mb-3"
										type="primary"
										icon={<DownloadOutlined />}
										onClick={this.onDownloadCsvClick}
									>
										Download Bordereau
									</Button>
								</Col>
							</Row>
							<Row>
								<Col xs={24}>
									<Table
										rowKey={(record) => record.id}
										columns={columns}
										dataSource={service.reportsList}
										pagination={service.pagination}
										loading={service.loading}
										onChange={this.handleTableChange}
									/>
								</Col>
							</Row>
						</Card>
					</Col>
				</Row>
			</DefaultLayout>
		);
	}

	handleTableChange = (pagination, filters, sorter) => {
		const service = this.props.context.reportsService;

		// Clear the reportsList to reset the data before fetching new data
		service.setReportsList([]);

		service.setPagination(pagination);
		service.setSorter(sorter);

		Object.entries(filters).forEach(([key, filter]) => {
			if (!filter || filter.length < 2) {
				service.removeFilter(key);
			} else {
				service.changeFilter(key, {
					type: "betweendate",
					field: key,
					values: filter,
				});
			}
		});

		// Fetch the new data
		service.loadReportsList();
	};



	onDownloadCsvClick = async () => {
		const reports = await this.props.context.reportsService.loadAllReports();


		const data = reports.map((row) => {
			return {
				"Policy Number": row.policy_number,
				"First Name": row.first_name,
				"Last Name": row.last_name,
				"Business Name": row.organisation_name,
				"Business Type": row.organisation_type,
				Email: row.email,
				Phone: row.phone,
				Postcode: row.postcode,
				"Flat/No": row.flat_no,
				"House Number and Street": row.address1,
				"Address 2": row.address2,
				"Address 3": row.address3,
				"City/Town": row.city,
				County: row.address5,
				"Payment Reference Number": row.payment_intent_id,
				Product: row.product_name,
				"ABC Policy Number": row.policy_number,
				Date: new Date(row.start_date).toLocaleDateString(),
				EndDate: row.end_date,
				Insurer: row.insurer,
				"Payment From": "Client",
				"Method of Payment": "Stripe",
				"Payment received": row.reconciled,
				"Payment Received Date": row.reconciled_on,
				"Due to Insurer": row.reconciled_on ? new Date(new Date(row.reconciled_on).setDate(new Date(row.reconciled_on).getDate() + 45)).toLocaleDateString() : "",
				"Paid to Insurer": "",
				"Paid to Insurer Date": "",
				"Payment Notes": "",
				"Transaction Type": row.status,
				Activities: row.primary_activity,
				Endorsements: row.endorsements,
				Turnover: row.pl_turnover,
				"PL Limit": row.pl_cover_amt,
				"PL Premium": row.pl_premium,
				Wageroll: row.el_wageroll,
				Volunteers: row.el_volunteer_staff,
				"EL Limit": row.el_cover_amt,
				"EL Premium": row.el_premium,
				"Activity Equipment": row.equipment,
				"MD Premium": row.equipment_premium,
				"Total Premium": row.gross_premium,
				"Insurance Premium Tax": row.ipt_amt,
				"Premium Including Tax": row.total,
				"Admin Fee": row.admin_fee,
				"Combined Total": row.total,
				"Commission to BF": row.agent_commission,
				"Premium to Insurer": row.net_premium,
				"To BF": row.toBf,
				"Total to Insurer": row.totalToInsurer,
			};
		});

		const csvReport = Papa.unparse(data, {
			quotes: true,
		});
		const csvReportBlob = new Blob([csvReport], {
			type: "text/csv;charset=utf-8",
		});

		return saveAs(csvReportBlob, "report.csv");
	};
}

export default Reports;
